import type { AxiosInstance } from 'axios'
import { wrapApiCall } from '@/lib/axios'
import { deserialize } from 'ts-jackson'
import {
  Branch,
  BranchReceiptSettings,
  type BranchListQuery,
  type BranchListResult,
  type CreateBranchPayload,
  type UpdateBranchPayload,
  type UpdateBranchReceiptSettingsPayload,
} from './model'

export const BranchService = (client: AxiosInstance) => {
  return {
    async listBranches(query: BranchListQuery = {}): Promise<BranchListResult> {
      const { data } = await wrapApiCall(() =>
        client.get<{
          data: Record<string, unknown>[]
          pagination: { total: number; page: number; pageSize: number }
        }>('/branches', {
          params: {
            page: query.page ?? 1,
            pageSize: query.pageSize ?? 20,
            ...(query.query ? { query: query.query } : {}),
          },
        })
      )
      return {
        data: (data?.data ?? []).map((item: Record<string, unknown>) => deserialize(item, Branch)),
        pagination: data?.pagination ?? { total: 0, page: 1, pageSize: 20 },
      }
    },
    async getBranch(id: number): Promise<Branch> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/branches/${id}`)
      )
      return deserialize(data, Branch)
    },
    async createBranch(payload: CreateBranchPayload): Promise<Branch> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/branches', payload)
      )
      return deserialize(data, Branch)
    },
    async updateBranch(id: number, payload: UpdateBranchPayload): Promise<Branch> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/branches/${id}`, payload)
      )
      return deserialize(data, Branch)
    },
    async getReceiptSettings(id: number): Promise<BranchReceiptSettings> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/branches/${id}/receipt-settings`)
      )
      return deserialize(data, BranchReceiptSettings)
    },
    async updateReceiptSettings(
      id: number,
      payload: UpdateBranchReceiptSettingsPayload
    ): Promise<BranchReceiptSettings> {
      const { data } = await wrapApiCall(() =>
        client.put<Record<string, unknown>>(`/branches/${id}/receipt-settings`, payload)
      )
      return deserialize(data, BranchReceiptSettings)
    },
    async deleteBranch(id: number): Promise<void> {
      await wrapApiCall(() => client.delete(`/branches/${id}`))
    },
    async getIsMultiBranch(): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.get<{ isMultiBranch: boolean }>('/branches/is-multi-branch')
      )
      return data?.isMultiBranch ?? false
    },
    /**
     * Fetch every branch in the tenant. Used by form pickers and the sidebar
     * selector — they need the complete list, not the first 100. Branch
     * counts are small in practice; we issue the first page synchronously
     * to learn `total`, then fan out the remaining pages in parallel so the
     * sidebar doesn't block on a sequential waterfall when a chain has more
     * than `pageSize` branches.
     */
    async getAllBranches(): Promise<Branch[]> {
      const pageSize = 100
      // Safety cap to prevent a runaway request fan-out on an unexpected
      // pagination bug.
      const MAX_PAGES = 50

      const fetchPage = async (page: number) => {
        const { data } = await wrapApiCall(() =>
          client.get<{
            data: Record<string, unknown>[]
            pagination: { total: number; page: number; pageSize: number }
          }>('/branches', { params: { page, pageSize } })
        )
        return {
          items: (data?.data ?? []).map((item) => deserialize(item, Branch)),
          total: data?.pagination?.total ?? 0,
        }
      }

      const first = await fetchPage(1)
      const all = [...first.items]
      if (first.total <= all.length) return all

      const lastPage = Math.min(MAX_PAGES, Math.ceil(first.total / pageSize))
      const restPages = Array.from(
        { length: lastPage - 1 },
        (_, i) => i + 2 // pages 2..lastPage
      )
      const rest = await Promise.all(restPages.map((p) => fetchPage(p)))
      for (const r of rest) all.push(...r.items)
      return all
    },
  }
}
